import React, { Component } from "react";
import { withRouter } from "react-router-dom";

import Backend from "../utils/Backend";
import Notify from "../utils/Notify";
import Currency from "../utils/Currency";
import General from "../utils/General";

class BasketDetails extends Component {
  constructor(props) {
    super(props);

    this.state = {
      basketId: props.match?.params?.basketId,
      basket: null,
      isLoading: true,
    };
  }

  componentDidMount() {
    this._loadBasket();
  }

  _loadBasket() {
    const { basketId } = this.state;

    this.setState({ isLoading: true });

    Backend.getBasket(basketId)
      .then((basket) => {
        this.setState({ basket, isLoading: false });
      })
      .catch((error) => {
        Notify.error(error.message || "Failed to load basket");
        this.setState({ isLoading: false });
      });
  }

  _toggleVisibility = () => {
    const { basket } = this.state;
    const newStatus = !basket.is_active;

    Backend.toggleBasketVisibility(basket.id, newStatus)
      .then(() => {
        Notify.success(
          newStatus ? "Basket activated" : "Basket deactivated"
        );
        this.setState({
          basket: { ...basket, is_active: newStatus },
        });
      })
      .catch((error) => {
        Notify.error(error.message || "Failed to update basket");
      });
  };

  _deleteBasket = () => {
    const { basket } = this.state;

    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${basket.name}"?\n\nThis action cannot be undone.`
    );

    if (!confirmDelete) {
      return;
    }

    Backend.deleteBasket(basket.id)
      .then(() => {
        Notify.success("Basket deleted successfully");
        this.props.history.push("/manage-baskets");
      })
      .catch((error) => {
        Notify.error(error.message || "Failed to delete basket");
      });
  };

  _goToEdit = () => {
    const { basketId } = this.state;
    this.props.history.push(`/manage-baskets/${basketId}/edit`);
  };

  _goBack = () => {
    this.props.history.push("/manage-baskets");
  };

  _getTypeLabel(type) {
    const typeLabels = {
      hamper: "Hamper",
      party_basket: "Party Basket",
      custom_bundle: "Custom Bundle",
    };
    return typeLabels[type] || type;
  }

  _getTypeColor(type) {
    const typeColors = {
      hamper: "label-light-primary",
      party_basket: "label-light-success",
      custom_bundle: "label-light-warning",
    };
    return typeColors[type] || "label-light-info";
  }

  _calculateTotalProductsValue() {
    const { basket } = this.state;
    if (!basket?.items) return 0;

    return basket.items.reduce((sum, item) => {
      const price = item.product?.price || item.price || 0;
      const qty = item.quantity || 1;
      return sum + price * qty;
    }, 0);
  }

  _renderBasketImage() {
    const { basket } = this.state;
    const image = basket?.image?.original || basket?.image?.thumbnail;
    const initials = General.getInitials(basket?.name);

    return (
      <div className="text-center mb-5">
        {image ? (
          <img
            src={image}
            alt={basket.name}
            className="rounded"
            style={{
              width: 200,
              height: 200,
              objectFit: "cover",
            }}
          />
        ) : (
          <div
            className="rounded bg-light-primary d-flex align-items-center justify-content-center"
            style={{ width: 200, height: 200, margin: "0 auto" }}
          >
            <span className="text-primary font-size-h1 font-weight-bold">
              {initials}
            </span>
          </div>
        )}
      </div>
    );
  }

  _renderProductsList() {
    const { basket } = this.state;

    if (!basket?.items || basket.items.length === 0) {
      return (
        <div className="text-center py-5">
          <p className="text-muted">No products in this basket.</p>
        </div>
      );
    }

    return (
      <div className="table-responsive">
        <table className="table table-head-custom table-vertical-center">
          <thead>
            <tr>
              <th>Product</th>
              <th style={{ width: "15%" }}>Unit Price</th>
              <th style={{ width: "10%" }}>Qty</th>
              <th style={{ width: "15%" }}>Subtotal</th>
            </tr>
          </thead>
          <tbody>
            {basket.items.map((item, index) => {
              const product = item.product || {};
              const price = product.price || item.price || 0;
              const qty = item.quantity || 1;
              const subtotal = price * qty;

              return (
                <tr key={index}>
                  <td>
                    <div className="d-flex align-items-center">
                      {product.image?.thumbnail && (
                        <div className="symbol symbol-40 symbol-light mr-3">
                          <img
                            src={product.image.thumbnail}
                            alt={product.title}
                            className="symbol-label"
                            style={{ objectFit: "cover" }}
                          />
                        </div>
                      )}
                      <div>
                        <span className="text-dark-75 font-weight-bold d-block">
                          {product.title || product.name || "Product"}
                        </span>
                        {product.description && (
                          <span className="text-muted font-size-sm">
                            {product.description.substring(0, 50)}...
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="text-dark-75">
                      {Currency.format(price)}
                    </span>
                  </td>
                  <td>
                    <span className="text-dark-75 font-weight-bold">{qty}</span>
                  </td>
                  <td>
                    <span className="text-dark-75 font-weight-bold">
                      {Currency.format(subtotal)}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-light">
              <td colSpan="3" className="text-right font-weight-bold">
                Products Total:
              </td>
              <td className="font-weight-bold text-primary">
                {Currency.format(this._calculateTotalProductsValue())}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  }

  render() {
    const { basket, isLoading } = this.state;

    if (isLoading) {
      return (
        <div className="container">
          <div className="card card-custom">
            <div className="card-body text-center py-10">
              <div className="spinner spinner-primary spinner-lg"></div>
              <p className="mt-5">Loading basket details...</p>
            </div>
          </div>
        </div>
      );
    }

    if (!basket) {
      return (
        <div className="container">
          <div className="card card-custom">
            <div className="card-body text-center py-10">
              <p className="text-muted">Basket not found.</p>
              <button
                type="button"
                className="btn btn-primary"
                onClick={this._goBack}
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      );
    }

    const hasDiscount =
      basket.discounted_price && basket.discounted_price < basket.price;

    return (
      <div className="container">
        {/* Header Card */}
        <div className="card card-custom mb-5">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-start">
              <div className="d-flex align-items-center">
                <button
                  type="button"
                  className="btn btn-icon btn-light mr-4"
                  onClick={this._goBack}
                >
                  <i className="fa fa-arrow-left"></i>
                </button>
                <div>
                  <h2 className="font-weight-bold mb-1">{basket.name}</h2>
                  <div className="d-flex align-items-center">
                    {/* <span
                      className={`label label-lg ${this._getTypeColor(
                        basket.type
                      )} label-inline mr-2`}
                    >
                      {this._getTypeLabel(basket.type)}
                    </span> */}
                    <span
                      className={`label label-lg ${
                        basket.is_active
                          ? "label-light-success"
                          : "label-light-danger"
                      } label-inline`}
                    >
                      {basket.is_active ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
              </div>
              <div>
                <button
                  type="button"
                  className="btn btn-light-primary mr-2"
                  onClick={this._goToEdit}
                >
                  <i className="fa fa-edit mr-2"></i>
                  Edit
                </button>
                <button
                  type="button"
                  className={`btn mr-2 ${
                    basket.is_active ? "btn-light-warning" : "btn-light-success"
                  }`}
                  onClick={this._toggleVisibility}
                >
                  {basket.is_active ? (
                    <>
                      <i className="fa fa-eye-slash mr-2"></i>
                      Deactivate
                    </>
                  ) : (
                    <>
                      <i className="fa fa-eye mr-2"></i>
                      Activate
                    </>
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-light-danger"
                  onClick={this._deleteBasket}
                >
                  <i className="fa fa-trash mr-2"></i>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          {/* Left Column - Details */}
          <div className="col-lg-8">
            {/* Description Card */}
            <div className="card card-custom mb-5">
              <div className="card-header">
                <div className="card-title">
                  <h3 className="card-label">Description</h3>
                </div>
              </div>
              <div className="card-body">
                <p className="text-dark-75">
                  {basket.description || "No description provided."}
                </p>
              </div>
            </div>

            {/* Products Card */}
            <div className="card card-custom mb-5">
              <div className="card-header">
                <div className="card-title">
                  <h3 className="card-label">
                    Products ({basket.items?.length || 0})
                  </h3>
                </div>
              </div>
              <div className="card-body">{this._renderProductsList()}</div>
            </div>
          </div>

          {/* Right Column - Image & Pricing */}
          <div className="col-lg-4">
            {/* Image Card */}
            <div className="card card-custom mb-5">
              <div className="card-header">
                <div className="card-title">
                  <h3 className="card-label">Basket Image</h3>
                </div>
              </div>
              <div className="card-body">{this._renderBasketImage()}</div>
            </div>

            {/* Pricing Card */}
            <div className="card card-custom mb-5">
              <div className="card-header">
                <div className="card-title">
                  <h3 className="card-label">Pricing</h3>
                </div>
              </div>
              <div className="card-body">
                <div className="d-flex justify-content-between mb-3">
                  <span className="text-muted">Products Value:</span>
                  <span className="text-dark-75">
                    {Currency.format(this._calculateTotalProductsValue())}
                  </span>
                </div>
                <div className="d-flex justify-content-between mb-3">
                  <span className="text-muted">Bundle Price:</span>
                  <span
                    className={`font-weight-bold ${
                      hasDiscount ? "text-muted text-decoration-line-through" : "text-primary"
                    }`}
                  >
                    {Currency.format(basket.price)}
                  </span>
                </div>
                {hasDiscount && (
                  <div className="d-flex justify-content-between mb-3">
                    <span className="text-muted">Sale Price:</span>
                    <span className="font-weight-bold text-success">
                      {Currency.format(basket.discounted_price)}
                    </span>
                  </div>
                )}
                {hasDiscount && (
                  <div className="d-flex justify-content-between pt-3 border-top">
                    <span className="font-weight-bold">You Save:</span>
                    <span className="font-weight-bold text-danger">
                      {Currency.format(basket.price - basket.discounted_price)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Restrictions Card */}
            <div className="card card-custom">
              <div className="card-header">
                <div className="card-title">
                  <h3 className="card-label">Purchase Restrictions</h3>
                </div>
              </div>
              <div className="card-body">
                <div className="d-flex justify-content-between mb-3">
                  <span className="text-muted">Min Quantity:</span>
                  <span className="text-dark-75 font-weight-bold">
                    {basket.min_purchase_qty || 1}
                  </span>
                </div>
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Max Quantity:</span>
                  <span className="text-dark-75 font-weight-bold">
                    {basket.max_purchase_qty || "No limit"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(BasketDetails);
